import React, { useMemo, useState } from 'react';
import { ArrowLeft } from './Icons';
import BottomNavigation from './BottomNavigation';
import { arPracticeData } from '../data/arPracticeData';
import { useSupabaseData } from '../hooks/useSupabaseData';

const ARPracticePage = ({ onBack, userPreferences }) => {
  const [selectedScene, setSelectedScene] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [arStatus, setArStatus] = useState('ready');
  
  const { displayLanguage } = userPreferences;

  const { data: remote } = useSupabaseData({
    table: 'eng_arpractice',
    mapRow: (row) => row,
    getFallback: () => null
  });

  const data = useMemo(() => {
    console.log('ARPracticePage - remote data:', remote);
    
    if (!remote || (Array.isArray(remote) && remote.length === 0)) {
      console.log('ARPracticePage - Using fallback data');
      return arPracticeData;
    }
    
    const rows = Array.isArray(remote) ? remote : [remote];
    const out = { overview: arPracticeData.overview };
    
    // Activation
    const activation = rows.find(r => r.data_type === 'activation');
    console.log('ARPracticePage - activation data:', activation);
    out.activationSection = activation ? {
      title: { 
        tc: activation.activation_title_tc || '', 
        sc: activation.activation_title_sc || '', 
        en: activation.activation_title_en || '' 
      },
      description: { 
        tc: activation.activation_description_tc || '', 
        sc: activation.activation_description_sc || '', 
        en: activation.activation_description_en || '' 
      },
      buttonText: { 
        tc: activation.activation_button_text_tc || '開始掃描', 
        sc: activation.activation_button_text_sc || '开始扫描', 
        en: activation.activation_button_text_en || 'Start Scanning' 
      }
    } : arPracticeData.activationSection;
    out.statusMessages = arPracticeData.statusMessages;
    
    // Scenes
    out.sceneSelection = { title: arPracticeData.sceneSelection.title, scenes: [] };
    rows.filter(r => r.data_type === 'scene').forEach((r, idx) => {
      console.log('ARPracticePage - processing scene:', r);
      out.sceneSelection.scenes.push({
        id: r.scene_id || r.id,
        icon: r.scene_icon || '🏬',
        iconText: r.scene_icon_text || null,
        title: { 
          tc: r.scene_title_tc || '', 
          sc: r.scene_title_sc || '', 
          en: r.scene_title_en || '' 
        },
        description: { 
          tc: r.scene_description_tc || '', 
          sc: r.scene_description_sc || '', 
          en: r.scene_description_en || '' 
        },
        estimatedDuration: { 
          tc: r.estimated_duration_tc || '10 分鐘', 
          sc: r.estimated_duration_sc || '10 分钟', 
          en: r.estimated_duration_en || '10 min' 
        },
        difficulty: r.difficulty || 'beginner',
        progress: r.progress || 0,
        isUnlocked: r.is_unlocked !== false
      });
    });
    
    // Highlights
    out.featureHighlights = { title: arPracticeData.featureHighlights.title, features: [] };
    rows.filter(r => r.data_type === 'feature').forEach((r) => {
      console.log('ARPracticePage - processing feature:', r);
      out.featureHighlights.features.push({
        id: r.feature_id || r.id,
        icon: r.feature_icon || '✨',
        iconColor: r.feature_icon_color || 'blue',
        title: { 
          tc: r.feature_title_tc || '', 
          sc: r.feature_title_sc || '', 
          en: r.feature_title_en || '' 
        },
        description: { 
          tc: r.feature_description_tc || '', 
          sc: r.feature_description_sc || '', 
          en: r.feature_description_en || '' 
        },
        isEnabled: r.is_enabled !== false
      });
    });
    
    console.log('ARPracticePage - processed data:', out);
    return out;
  }, [remote]);

  const getText = (textObj) => {
    return textObj[displayLanguage] || textObj.en;
  };

  const handleStartScanning = () => {
    setIsScanning(true);
    setArStatus('scanning');
    
    // Simulate scanning process
    setTimeout(() => {
      setIsScanning(false);
      setArStatus('ready');
    }, 3000);
  };

  const handleSceneClick = (scene) => {
    if (scene.isUnlocked) {
      setSelectedScene(scene);
    }
  };

  const handleStartARSession = () => {
    if (selectedScene) {
      console.log('Starting AR session for:', selectedScene.id);
      // Handle AR session start
    }
  };

  const getIconColor = (iconColor) => {
    switch (iconColor) {
      case 'blue': return 'text-blue-400';
      case 'purple': return 'text-purple-400';
      case 'green': return 'text-green-400';
      default: return 'text-white';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-500';
      case 'intermediate': return 'bg-yellow-500';
      case 'advanced': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="px-6 pt-12 pb-4">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-white">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold">
            {getText(data.overview.title)}
          </h1>
          <div className="w-6"></div> {/* Spacer for centering */}
        </div>
      </div>

      {/* AR Mode Activation Section */}
      <div className="px-6 mb-8">
        <div className="text-center">
          {/* Large Circular Camera Button */}
          <div className="flex justify-center mb-4">
            <button
              onClick={handleStartScanning}
              disabled={isScanning}
              className={`w-24 h-24 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-3xl shadow-lg transition-all duration-300 ${
                isScanning ? 'animate-pulse' : 'hover:scale-105'
              }`}
            >
              {isScanning ? '⏳' : '📷'}
            </button>
          </div>
          
          {/* Activation Title */}
          <h2 className="text-lg font-semibold mb-2">
            {getText(data.activationSection.title)}
          </h2>
          
          {/* Description */}
          <p className="text-gray-300 text-sm mb-4">
            {getText(data.activationSection.description)}
          </p>
          
          {/* Start Scanning Button */}
          <button
            onClick={handleStartScanning}
            disabled={isScanning}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {isScanning ? getText(data.statusMessages.scanning) : getText(data.activationSection.buttonText)}
          </button>
        </div>
      </div>

      {/* AR Scene Selection Section */}
      <div className="px-6 mb-8">
        <h2 className="text-lg font-semibold mb-4">
          {getText(data.sceneSelection.title)}
        </h2>
        
        <div className="grid grid-cols-2 gap-4">
          {data.sceneSelection.scenes.map((scene) => (
            <div
              key={scene.id}
              onClick={() => handleSceneClick(scene)}
              className={`bg-blue-800/30 border border-blue-600/30 rounded-xl p-4 cursor-pointer transition-all ${
                scene.isUnlocked 
                  ? 'hover:bg-blue-700/50 hover:border-blue-500' 
                  : 'opacity-60 cursor-not-allowed'
              }`}
            >
              {/* Scene Icon and Text */}
              <div className="flex items-center justify-center mb-3">
                <div className="text-3xl relative">
                  {scene.icon}
                  {scene.iconText && (
                    <span className="absolute -top-1 -right-1 text-xs bg-red-500 text-white px-1 rounded">
                      {scene.iconText}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Scene Title */}
              <h3 className="font-semibold text-center mb-1">
                {getText(scene.title)}
              </h3>
              
              {/* Scene Description */}
              <p className="text-gray-300 text-xs text-center">
                {getText(scene.description)}
              </p>
              
              {/* Difficulty Badge */}
              <div className="flex justify-center mt-2">
                <span className={`${getDifficultyColor(scene.difficulty)} text-white text-xs px-2 py-1 rounded-full`}>
                  {scene.difficulty.toUpperCase()}
                </span>
              </div>
              
              {/* Progress Bar */}
              <div className="mt-3">
                <div className="w-full bg-gray-700 rounded-full h-1">
                  <div 
                    className="bg-green-500 h-1 rounded-full transition-all duration-300"
                    style={{ width: `${scene.progress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-400 text-center mt-1">
                  {scene.progress}%
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AR Feature Highlights Section */}
      <div className="px-6 mb-8">
        <h2 className="text-lg font-semibold mb-4">
          {getText(data.featureHighlights.title)}
        </h2>
        
        <div className="space-y-4">
          {data.featureHighlights.features.map((feature) => (
            <div key={feature.id} className="flex items-center space-x-4">
              {/* Feature Icon */}
              <div className={`text-2xl ${getIconColor(feature.iconColor)}`}>
                {feature.icon}
              </div>
              
              {/* Feature Content */}
              <div className="flex-1">
                <h3 className="font-semibold text-sm">
                  {getText(feature.title)}
                </h3>
                <p className="text-gray-300 text-xs">
                  {getText(feature.description)}
                </p>
              </div>
              
              {/* Feature Status */}
              <div className={`w-3 h-3 rounded-full ${feature.isEnabled ? 'bg-green-500' : 'bg-gray-500'}`}></div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation
        currentView="ar_practice"
        onHomeClick={onBack}
        onLearningClick={onBack}
        onChallengeClick={onBack}
        onSocialClick={onBack}
      />

      {/* Scene Selection Modal */}
      {selectedScene && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold mb-4">
              {getText(selectedScene.title)}
            </h3>
            
            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-3">
                <span className="text-gray-400">Description:</span>
                <span className="text-sm">{getText(selectedScene.description)}</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-gray-400">Duration:</span>
                <span>{getText(selectedScene.estimatedDuration)}</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-gray-400">Difficulty:</span>
                <span className="capitalize">{selectedScene.difficulty}</span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-gray-400">Progress:</span>
                <span>{selectedScene.progress}%</span>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setSelectedScene(null)}
                className="flex-1 py-2 px-4 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStartARSession}
                className="flex-1 py-2 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Start AR Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ARPracticePage;
